import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { AccessControlService } from '@/services';
import { QueryParams } from '@/model';
import { CreateAccessControlUserPayload, CreatePermissionTypePayload, UpdateAccessControlUserPayload } from '../request/access-control';

export const useAccessControlPermissionTypes = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_ACCESS_CONTROL_PERMISSION_TYPES', params],
    async () => {
      const [err, data] = await AccessControlService.getPermissionTypes(params);
      if (err) {
        throw err;
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

export const useCreateAccessControlPermissionTypeMutation = () => {
  return useMutation({
    mutationKey: ['CREATE_ACCESS_CONTROL_PERMISSION_TYPE'],
    mutationFn: async (payload: CreatePermissionTypePayload) => {
      const [err, data] = await AccessControlService.createPermissionType(payload);
      if (err) throw err;
      return data;
    },
  });
};

export const useAccessControlPermissionTypeById = (id: string) => {
  const query = useQuery(
    ['GET_ACCESS_CONTROL_PERMISSION_TYPE_BY_ID', id],
    async () => {
      const [err, data] = await AccessControlService.getPermissionTypeById(id);
      if (err) {
        throw err;
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

type UpdatePermissionTypeMutationParams = { id: string; payload: CreatePermissionTypePayload };
export const useUpdatePermissionTypeMutation = () => {
  return useMutation({
    mutationKey: ['UPDATE_ACCESS_CONTROL_PERMISSION_TYPE'],
    mutationFn: async ({ id, payload }: UpdatePermissionTypeMutationParams) => {
      const [err, data] = await AccessControlService.updatePermissionType(id, payload);
      if (err) throw err;
      return data;
    },
  });
};

export const useAccessControlUsers = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_ACCESS_CONTROL_USERS', params],
    async () => {
      const [err, data] = await AccessControlService.getUsers(params);
      if (err) {
        throw err;
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

export const useCreateAccessControlUserMutation = () => {
  return useMutation({
    mutationKey: ['CREATE_ACCESS_CONTROL_USER'],
    mutationFn: async (payload: CreateAccessControlUserPayload) => {
      const [err, data] = await AccessControlService.createUser(payload);
      if (err) throw err;
      return data;
    },
  });
};

export const useAccessControlUserById = (id: string) => {
  const query = useQuery(
    ['GET_ACCESS_CONTROL_USER_BY_ID', id],
    async () => {
      const [err, data] = await AccessControlService.getUserById(id);
      if (err) {
        throw err;
      }
      return data;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);

  return returnQuery;
};

type UpdateAccessControlUserMutationParams = { id: string; payload: UpdateAccessControlUserPayload };
export const useUpdateAccessControlUserMutation = () => {
  return useMutation({
    mutationKey: ['UPDATE_ACCESS_CONTROL_USER'],
    mutationFn: async ({ id, payload }: UpdateAccessControlUserMutationParams) => {
      const [err, data] = await AccessControlService.updateUser(id, payload);
      if (err) throw err;
      return data;
    },
  });
};

export const useBlockAccessControlUser = () => {
  return useMutation({
    mutationKey: ['BLOCK_ACCESS_CONTROL_USER'],
    mutationFn: async (id: string) => {
      const [err, data] = await AccessControlService.blockUser(id);
      if (err) throw err;
      return data;
    },
  });
};

export const useUnblockAccessControlUser = () => {
  return useMutation({
    mutationKey: ['UNBLOCK_ACCESS_CONTROL_USER'],
    mutationFn: async (id: string) => {
      const [err, data] = await AccessControlService.unblockUser(id);
      if (err) throw err;
      return data;
    },
  });
};
